import React, { useMemo, useState } from "react";

import {
  ArrayUtils,
  formatDayDate,
  formatNumber,
  getCropName,
  useFertilizerHelpers,
  useGetFieldPractices,
  useGetPracticeOrganicFertilizerApplications,
  useInView,
  useUpdateVerificationStatus,
} from "utils";

import { CropFCPO, FieldVerification, PRACTICE_SLUG, PracticeFCPO, VerificationStatus, ZERO_UUID } from "models";

import { Col, Icon, Message, Row, SkeletonWrapper, Tooltip } from "components";

import { CropDrawer } from "pages/admin/verifications/components/misc/CropDrawer";
import { VerificationStatusDropdown } from "pages/admin/verifications/components/misc/VerificationStatusDropdown";

interface FertilizerTableProps {
  field: FieldVerification;
  farmId: string;
}

export const FertilizerTable = ({ field, farmId }: FertilizerTableProps) => {
  const [cropToEdit, setCropToEdit] = useState<CropFCPO | null>(null);
  const { getFertilizerName, getFertilizerNValue } = useFertilizerHelpers();
  const { data: applicationTypes } = useGetPracticeOrganicFertilizerApplications({ enabled: true });

  // Helper function to get application type name
  const getApplicationTypeName = (applicationTypeId?: string) => {
    if (!applicationTypeId) return null;
    return applicationTypes?.find((a) => a.id === applicationTypeId)?.name ?? null;
  };

  const fieldId = field.id;
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const { data: fieldPractices, isLoading } = useGetFieldPractices({ fieldId, inView, isAdmin: true });

  // Handler for clicking on a fertilizer row to open the crop drawer
  const handleFertilizerClick = (fertilizer: PracticeFCPO) => {
    const associatedCrop = fieldPractices?.crops?.find((c) => c.id === fertilizer.cropId);
    if (associatedCrop) {
      setCropToEdit(associatedCrop);
    }
  };

  const { mutate: updateStatus } = useUpdateVerificationStatus({ fieldId, farmId });

  // Handler for updating fertilizer verification status
  const handleStatusChange = (fertilizerId: string, fertilizerType: string, newStatus: VerificationStatus) => {
    if (!newStatus) return;

    updateStatus(
      {
        practiceType: fertilizerType,
        practiceId: fertilizerId,
        verificationStatus: newStatus,
      },
      {
        onSuccess: () => {
          Message({ icon: "tick", content: "Fertilizer verification status updated successfully" });
        },
        onError: () => {
          Message({ icon: "error", content: "Failed to update fertilizer verification status" });
        },
      }
    );
  };

  // Filter practices to get only fertilizers
  const fertilizers = fieldPractices?.practices?.filter(
    (practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS || practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS
  );

  // Easter egg: Clemens' Cozy Cottage - 1/20 chance for self-produced organic fertilizer to show a golden home
  const cozyHomeMap = useMemo(() => {
    const map = new Map<string, boolean>();
    fertilizers?.forEach((fertilizer) => {
      if (fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && fertilizer.selfProduced === true && fertilizer.id) {
        const isCozyHome = Math.random() < 1 / 20;
        if (isCozyHome) {
          map.set(fertilizer.id, true);
        }
      }
    });
    return map;
  }, [fertilizers]);

  // Group fertilizers by year
  const fertilizersByYear = ArrayUtils.groupBy(fertilizers ?? [], (fertilizer) => fertilizer?.year ?? "unknown");

  // Sort years in descending order
  const sortedYears = Object.keys(fertilizersByYear || {})
    .filter((year) => year !== "unknown")
    .map(Number)
    .sort((a, b) => b - a);

  return (
    <div ref={ref} className="crop-table">
      <SkeletonWrapper isLoading={isLoading} type="block" height="20rem">
        {/* Table Header */}
        <Row className="crop-table-header">
          <Col md={5} className="header-cell">
            fertilizer name
          </Col>
          <Col md={3} className="header-cell">
            amount
          </Col>
          <Col md={2} className="header-cell">
            N-Value
          </Col>
          <Col md={3} className="header-cell">
            date
          </Col>
          <Col md={3} className="header-cell">
            application type
          </Col>
          <Col md={3} className="header-cell">
            self produced
          </Col>
          <Col md={4} className="header-cell">
            status
          </Col>
          <Col md={1} className="header-cell"></Col>
        </Row>

        {/* Table Body grouped by year */}
        {sortedYears.map((year) => {
          // Sort fertilizers by application date (endDate) in descending order (most recent first)
          const yearFertilizers = [...fertilizersByYear[year]].sort((a, b) => {
            if (!a.endDate && !b.endDate) return 0;
            if (!a.endDate) return 1;
            if (!b.endDate) return -1;
            return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
          });
          return (
            <div key={year} className="year-group">
              {/* Year header */}
              <Row className="year-header" align="middle">
                <Col md={5}>
                  <h4 className="year-title">{year}</h4>
                </Col>
                <Col md={3}></Col>
                <Col md={2}></Col>
                <Col md={3}></Col>
                <Col md={3}></Col>
                <Col md={3}></Col>
                <Col md={4}></Col>
                <Col md={1}></Col>
              </Row>

              {/* Fertilizers for this year */}
              {yearFertilizers.map((fertilizer) => {
                const cropName = getCropName(fertilizer.cropId, fieldPractices);
                const isPurchasedOrganicFertilizer =
                  fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && fertilizer.selfProduced === false && fertilizer.assignedPracticeId !== ZERO_UUID;
                return (
                  <Row
                    key={fertilizer.id}
                    className="crop-row pointer"
                    onClick={() => handleFertilizerClick(fertilizer)}
                    style={isPurchasedOrganicFertilizer ? { borderLeft: "3px solid #e65100" } : undefined}
                  >
                    <Col md={5} className="crop-cell">
                      <div className="crop-name-cell">
                        <div className={`crop-indicator ${fertilizer.verificationStatus}`} />
                        <Icon
                          iconName={
                            fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS
                              ? fertilizer.assignedPracticeId === ZERO_UUID
                                ? "tabler/droplet-off"
                                : "tabler/droplet"
                              : "tabler/poo"
                          }
                          fill={fertilizer.assignedPracticeId !== ZERO_UUID ? "currentColor" : "none"}
                          className="icon inline margin-right-50"
                          size="s"
                        />
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
                          <span className="paragraph-s-bold">{getFertilizerName(fertilizer.type, fertilizer.assignedPracticeId)}</span>
                          {cropName && <span className="paragraph-xs grey-text">{cropName}</span>}
                        </div>
                      </div>
                    </Col>
                    <Col md={3} className="crop-cell">
                      <span className="paragraph-xs">
                        {fertilizer.assignedPracticeId === ZERO_UUID || !fertilizer.amount
                          ? "-"
                          : `${formatNumber({ number: fertilizer.amount, decimalPlaces: 2 })} kg/ha`}
                      </span>
                    </Col>
                    <Col md={2} className="crop-cell">
                      <span className="paragraph-xs">
                        {fertilizer.assignedPracticeId === ZERO_UUID
                          ? "-"
                          : (() => {
                              const nValue = getFertilizerNValue(fertilizer);
                              return nValue !== null && nValue !== undefined ? `${formatNumber({ number: nValue, decimalPlaces: 2 })} kg/ha` : "-";
                            })()}
                      </span>
                    </Col>
                    <Col md={3} className="crop-cell">
                      <span className="paragraph-xs">
                        {fertilizer.assignedPracticeId === ZERO_UUID ? "-" : fertilizer.endDate ? formatDayDate(fertilizer.endDate) : "-"}
                      </span>
                    </Col>
                    <Col md={3} className="crop-cell">
                      <span className="paragraph-xs">
                        {fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && fertilizer.assignedPracticeId !== ZERO_UUID
                          ? getApplicationTypeName(fertilizer.applicationType) ?? "-"
                          : "-"}
                      </span>
                    </Col>
                    <Col md={3} className="crop-cell">
                      {fertilizer.assignedPracticeId === ZERO_UUID || fertilizer.selfProduced == null ? (
                        <span className="paragraph-xs">-</span>
                      ) : fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS ? (
                        (() => {
                          const isCozyHome = fertilizer.id && cozyHomeMap.has(fertilizer.id);
                          const cozyTooltip = isCozyHome
                            ? "🏡 You found Clemens' Place! Wait... which Clemens? Both? The mystery deepens. +15 Confusion Resistance, +10 Self-Produced Pride"
                            : "";
                          const colorClass = fertilizer.selfProduced ? (isCozyHome ? "orange-text" : "green-text") : "orange-text";
                          return (
                            <Tooltip title={cozyTooltip}>
                              <span
                                className={`paragraph-xs ${colorClass}`}
                                style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
                                title={!isCozyHome ? (fertilizer.selfProduced ? "Produced on the farm" : "Purchased / brought from outside") : undefined}
                              >
                                <Icon iconName={fertilizer.selfProduced ? "tabler/home" : "tabler/truck"} size="s" className="icon inline" />
                                {fertilizer.selfProduced ? "Yes" : "No"}
                              </span>
                            </Tooltip>
                          );
                        })()
                      ) : (
                        <span className="paragraph-xs">{fertilizer.selfProduced ? "Yes" : "No"}</span>
                      )}
                    </Col>
                    <Col md={4} className="crop-cell">
                      <VerificationStatusDropdown
                        status={fertilizer.verificationStatus ?? null}
                        cropId={fertilizer.id!}
                        onStatusChange={(cropId, status) => handleStatusChange(cropId, fertilizer.type, status)}
                      />
                    </Col>
                    <Col md={1} className="crop-cell"></Col>
                  </Row>
                );
              })}
            </div>
          );
        })}

        {/* Empty state */}
        {sortedYears.length === 0 && !isLoading && (
          <Row className="empty-state">
            <Col md={24}>
              <span className="paragraph-s text-disabled">No fertilizers found for this field</span>
            </Col>
          </Row>
        )}
      </SkeletonWrapper>

      <CropDrawer
        crop={cropToEdit}
        showDrawer={!!cropToEdit}
        onClose={() => setCropToEdit(null)}
        fieldPractices={fieldPractices}
        field={field}
        farmId={farmId}
      />
    </div>
  );
};
